/**
 * /akun/parent-zone — Profil dan Preferensi home (Phase 5.3).
 *
 * PIN-unlocked landing. 6 menu sections + sticky "Kunci Profil dan Preferensi" action.
 * Per doc 08 §3:
 *   1. Profil Anak (Edit) → /akun/edit-profil
 *   2. Preferensi (Edit)   → /akun/edit-preferensi
 *   3. Multi-Anak (Soon)   → placeholder (Y2)
 *   4. Detail Aktivitas    → /library
 *   5. Pengaturan          → /akun/pengaturan
 *   6. Tentang Aplikasi    → /akun/tentang
 *   7. Hapus Data Saya     → /akun/hapus-data
 *
 * "Hapus Data Saya" gets a red border per doc 08 §3.2.
 *
 * Session check: on mount, load the stored session. If invalid, redirect
 * back to /akun/pin. We don't hit the network on every mount (the
 * session is 15min, plenty long for the user to navigate within the
 * zone).
 */

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'expo-router';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View, type ImageSourcePropType } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { AppIcon } from '@/components/app-icon';
import { Brand, Radii, Spacing } from '@/constants/theme';
import { MISC_ICON, TEMA_ICON } from '@/lib/icons';
import { getActiveChild } from '@/lib/api/children';
import { clearSession, loadSession } from '@/lib/parent-session';
import { isGuestSession } from '@/lib/auth';
import { preferencesVisibleForTier, tierForSession } from '@/lib/premium';
import { isPremiumCached, refreshPremiumStatus } from '@/lib/premium-status';

import type { ChildProfile } from '@funpaper/shared';

type State =
  | { kind: 'loading' }
  | { kind: 'unlocked'; child: ChildProfile | null; isGuest: boolean }
  | { kind: 'no_session' };

export default function ParentZoneScreen() {
  const router = useRouter();
  const [state, setState] = useState<State>({ kind: 'loading' });
  // Fase 2 plan 23: gerbang Preferensi berbasis entitlement — cache untuk
  // render pertama, lalu refresh dari server saat mount supaya tidak stale.
  const [isPremium, setIsPremium] = useState(() => isPremiumCached());

  useEffect(() => {
    let cancelled = false;
    void refreshPremiumStatus().then((v) => {
      if (!cancelled) setIsPremium(v);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    (async () => {
      const guest = isGuestSession();
      if (!guest) {
        const session = await loadSession();
        if (!session) {
          setState({ kind: 'no_session' });
          // Redirect to PIN flow.
          router.replace('/akun/pin' as never);
          return;
        }
      }
      // Load child in background.
      const child = await getActiveChild().catch(() => null);
      setState({ kind: 'unlocked', child, isGuest: guest });
    })();
  }, [router]);

  const onLock = useCallback(async () => {
    await clearSession();
    router.replace('/akun' as never);
  }, [router]);

  if (state.kind === 'loading' || state.kind === 'no_session') {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <View style={styles.center}>
            <ActivityIndicator size="large" color={Brand.primary} />
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  const { child, isGuest } = state;
  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
        <ScrollView contentContainerStyle={styles.scrollContent}>
          <View style={styles.header}>
            <Pressable
              onPress={() => router.back()}
              style={({ pressed }) => [styles.backBtn, pressed && styles.btnPressed]}
              accessibilityRole="button"
              accessibilityLabel="Kembali ke Akun"
            >
              <Text style={styles.backArrow}>←</Text>
            </Pressable>
            <View style={{ flex: 1 }}>
              <ThemedText type="subtitle" style={styles.headerTitle}>
                Profil dan Preferensi
              </ThemedText>
              <ThemedText type="small" themeColor="textSecondary">
                {isGuest ? 'Mode eksplorasi tanpa PIN' : 'Kelola profil anak dan pengaturan aplikasi'}
              </ThemedText>
            </View>
          </View>

          {/* 1. Profil Anak */}
          <MenuItem
            icon={TEMA_ICON['Diri Sendiri']}
            title="Profil Anak"
            subtitle={child ? `${child.name} · ${child.age} tahun` : 'Belum ada profil'}
            trailing="Edit"
            onPress={() => router.push('/akun/edit-profil' as never)}
          />

          {/* 2. Preferensi — Fase 2 plan 23: hanya tier premium (entitlement
              per akun, bukan flag global). Server menegaskan di PUT. */}
          {preferencesVisibleForTier(tierForSession({ isGuest, isPremium })) ? (
            <MenuItem
              icon={MISC_ICON.star}
              title="Preferensi"
              subtitle="Umur, aktivitas, tingkat kesulitan, dan jumlah worksheet/hari"
              trailing="Edit"
              onPress={() => router.push('/akun/edit-preferensi' as never)}
            />
          ) : null}

          {/* 3. Multi-Anak (Y2 placeholder) */}
          <MenuItem
            icon={TEMA_ICON['Keluarga']}
            title="Multi-Anak"
            subtitle="Akan datang di update berikutnya"
            trailing="Soon"
            onPress={() => {}}
            disabled
          />

          {/* 4. Detail Aktivitas */}
          <MenuItem
            icon={MISC_ICON.books}
            title="Detail Aktivitas"
            subtitle="30 hari worksheet yang sudah dipilih"
            trailing="Buka"
            onPress={() => router.push('/library' as never)}
          />

          {/* Pengaturan dan hapus data membutuhkan PIN akun penuh. */}
          {!isGuest ? (
            <MenuItem
              title="Pengaturan"
              subtitle="Bahasa, tema tampilan, PIN"
              trailing="Buka"
              onPress={() => router.push('/akun/pengaturan' as never)}
            />
          ) : null}

          {/* 6. Tentang Aplikasi */}
          <MenuItem
            title="Tentang Aplikasi"
            subtitle="Versi, kebijakan privasi, kontak"
            trailing="Buka"
            onPress={() => router.push('/akun/tentang' as never)}
          />

          {!isGuest ? (
            <MenuItem
              title="Hapus Data Saya"
              subtitle="Hapus semua worksheet dan preferensi"
              trailing="›"
              destructive
              onPress={() => router.push('/akun/hapus-data' as never)}
            />
          ) : null}

          {/* Kunci Profil dan Preferensi (sticky action) */}
          <Pressable
            onPress={onLock}
            style={({ pressed }) => [styles.lockBtn, pressed && styles.btnPressed]}
            accessibilityRole="button"
            accessibilityLabel={isGuest ? 'Kembali ke Akun' : 'Kunci Profil dan Preferensi'}
          >
            <Text style={styles.lockBtnText}>
              {isGuest ? 'Kembali ke Akun' : 'Kunci Profil dan Preferensi'}
            </Text>
          </Pressable>
        </ScrollView>
      </SafeAreaView>
    </ThemedView>
  );
}

// ----------------------------------------------------------------------------
// MenuItem
// ----------------------------------------------------------------------------

interface MenuItemProps {
  icon?: ImageSourcePropType;
  title: string;
  subtitle: string;
  trailing: string;
  destructive?: boolean;
  disabled?: boolean;
  onPress: () => void;
}

function MenuItem({
  icon,
  title,
  subtitle,
  trailing,
  destructive = false,
  disabled = false,
  onPress,
}: MenuItemProps) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={({ pressed }) => [
        styles.menuItem,
        destructive && styles.menuItemDestructive,
        disabled && styles.menuItemDisabled,
        pressed && !disabled && styles.menuItemPressed,
      ]}
      accessibilityRole="button"
      accessibilityLabel={title}
      accessibilityState={{ disabled }}
    >
      {icon ? (
        <View style={styles.menuIconWrap}>
          <AppIcon source={icon} size={24} />
        </View>
      ) : null}
      <View style={styles.menuContent}>
        <ThemedText
          type="smallBold"
          style={destructive ? styles.menuTitleDestructive : styles.menuTitle}
        >
          {title}
        </ThemedText>
        <ThemedText type="small" themeColor="textSecondary">
          {subtitle}
        </ThemedText>
      </View>
      <View
        style={[
          styles.trailing,
          trailing === 'Edit' && styles.trailingEdit,
          trailing === 'Soon' && styles.trailingSoon,
        ]}
      >
        <Text
          style={[
            styles.trailingText,
            trailing === 'Edit' && styles.trailingTextEdit,
            trailing === 'Soon' && styles.trailingTextSoon,
          ]}
        >
          {trailing}
        </Text>
      </View>
    </Pressable>
  );
}

// ----------------------------------------------------------------------------
// Styles
// ----------------------------------------------------------------------------

const styles = StyleSheet.create({
  root: { flex: 1 },
  safe: { flex: 1 },
  scrollContent: {
    padding: Spacing.four,
    paddingBottom: Spacing.six,
    gap: Spacing.two,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.three,
    marginBottom: Spacing.two,
  },
  backBtn: {
    width: 40, height: 40,
    alignItems: 'center', justifyContent: 'center',
    borderRadius: Radii.small,
  },
  backArrow: { fontSize: 24, color: Brand.primary, fontWeight: '600' },
  headerTitle: { fontSize: 18, fontWeight: '700', color: '#1A0B5C' },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e5e5',
    borderRadius: Radii.medium,
    padding: Spacing.three,
    gap: Spacing.three,
  },
  menuItemDestructive: {
    borderColor: '#ef4444',
    borderWidth: 2,
  },
  menuItemDisabled: { opacity: 0.5 },
  menuItemPressed: { backgroundColor: '#f5f3ee' },
  menuIconWrap: {
    width: 48, height: 48, borderRadius: 24,
    backgroundColor: 'rgba(86, 63, 122, 0.10)',
    alignItems: 'center', justifyContent: 'center',
  },
  menuContent: { flex: 1 },
  menuTitle: { color: '#1A0B5C' },
  menuTitleDestructive: { color: '#c2410c' },
  trailing: {
    paddingHorizontal: 10, paddingVertical: 4,
    borderRadius: 999,
  },
  trailingEdit: { backgroundColor: Brand.primary },
  trailingSoon: { backgroundColor: '#F3F4F6' },
  trailingText: { fontSize: 12, fontWeight: '600' },
  trailingTextEdit: { color: '#ffffff' },
  trailingTextSoon: { color: '#6B7280' },
  lockBtn: {
    backgroundColor: Brand.primary,
    paddingVertical: 14,
    borderRadius: Radii.medium,
    alignItems: 'center',
    marginTop: Spacing.three,
  },
  lockBtnText: { color: '#ffffff', fontSize: 16, fontWeight: '700' },
  btnPressed: { opacity: 0.7 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
